import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ARTIFACT_STEP_MATRIX,
  ARTIFACT_TYPES,
  FLOW3_RENDER_BRANCH,
  FLOW3_STEPS,
  artifactTypeIdFromArtifactId,
} from '@/lib/learningTutorial/flow3ArtifactPipelineMap';

function CellDetail({ cell, expanded }) {
  if (!cell) {
    return <span className="text-slate-600">—</span>;
  }

  return (
    <div className="space-y-1">
      <p className={cn('text-[11px] leading-snug', expanded ? 'text-slate-200' : 'text-slate-400 line-clamp-3')}>
        {cell.summary}
      </p>
      {expanded ? (
        <>
          <p className="text-[10px] font-mono text-violet-300/80">{cell.outputShape}</p>
          <ul className="space-y-0.5">
            {cell.backendFiles.map((file) => (
              <li key={file} className="text-[10px] font-mono text-emerald-400/70 flex items-start gap-1">
                <ExternalLink className="w-2.5 h-2.5 shrink-0 mt-0.5 opacity-60" />
                <span className="break-all">{file}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function FlowMapCell({ cell, isHighlighted, isExpanded, onToggle }) {
  return (
    <td
      className={cn(
        'align-top border border-violet-500/10 px-2 py-2 min-w-[140px] max-w-[200px] cursor-pointer transition-colors',
        isHighlighted ? 'bg-violet-600/20 ring-1 ring-inset ring-violet-400/40' : 'bg-slate-950/30 hover:bg-slate-900/50',
      )}
      onClick={onToggle}
      title={cell?.summary}
    >
      <CellDetail cell={cell} expanded={isExpanded} />
    </td>
  );
}

export function Flow3ArtifactPipelineMap({
  highlightedStepId = null,
  highlightedArtifactId = null,
  onStepSelect,
}) {
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [expandedCell, setExpandedCell] = useState(null);

  const highlightedTypeId = useMemo(
    () => artifactTypeIdFromArtifactId(highlightedArtifactId),
    [highlightedArtifactId],
  );

  const visibleTypes = useMemo(
    () => (showAllTypes ? ARTIFACT_TYPES : ARTIFACT_TYPES.filter((t) => t.featured)),
    [showAllTypes],
  );

  const toggleCell = (typeId, stepId) => {
    const key = `${typeId}:${stepId}`;
    setExpandedCell((prev) => (prev === key ? null : key));
  };

  return (
    <div className="space-y-3" data-testid="flow3-artifact-pipeline-map">
      <div className="rounded-lg border border-violet-500/20 bg-violet-950/15 px-3 py-2">
        <p className="text-xs text-violet-200/90">
          Trace what happens to each artifact type at every Flow 3 step. Click a cell for JSON shape and backend files.
        </p>
        {highlightedArtifactId && highlightedTypeId ? (
          <p className="text-[11px] text-emerald-400/90 mt-1">
            Highlighting row for <span className="font-mono">{highlightedArtifactId}</span>
          </p>
        ) : null}
      </div>

      <div className="rounded-lg border border-slate-700/50 bg-slate-950/40 px-3 py-2">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">5C.5 render branch</p>
        <pre className="text-[10px] font-mono text-slate-400 leading-relaxed whitespace-pre-wrap">
          {FLOW3_RENDER_BRANCH.join('\n')}
        </pre>
      </div>

      <button
        type="button"
        className="flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200"
        onClick={() => setShowAllTypes((v) => !v)}
      >
        {showAllTypes ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {showAllTypes ? 'Show featured types only' : 'Show all artifact types (FLW, TBL)'}
      </button>

      <div className="overflow-auto max-h-[52vh] rounded-lg border border-violet-500/15">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-900/95 backdrop-blur-sm">
              <th className="sticky left-0 z-20 bg-slate-900/95 border border-violet-500/15 px-3 py-2 text-[10px] uppercase tracking-wide text-slate-400 min-w-[120px]">
                Artifact
              </th>
              {FLOW3_STEPS.map((step) => {
                const isColHighlight = step.id === highlightedStepId;
                return (
                  <th
                    key={step.id}
                    className={cn(
                      'border border-violet-500/15 px-2 py-2 text-center min-w-[72px]',
                      isColHighlight ? 'bg-violet-600/25 text-violet-100' : 'text-slate-400',
                    )}
                  >
                    <button
                      type="button"
                      className="w-full text-[10px] font-semibold hover:text-violet-200"
                      onClick={() => onStepSelect?.(step.id)}
                      title={step.label}
                    >
                      {step.shortLabel}
                    </button>
                    <p className="text-[9px] font-normal text-slate-500 mt-0.5 line-clamp-2">{step.label}</p>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visibleTypes.map((artifactType) => {
              const isRowHighlight = artifactType.id === highlightedTypeId;
              const rowCells = ARTIFACT_STEP_MATRIX[artifactType.id] || {};
              return (
                <tr
                  key={artifactType.id}
                  className={cn(isRowHighlight && 'bg-emerald-950/20')}
                  data-testid={`flow-map-row-${artifactType.id}`}
                >
                  <th
                    className={cn(
                      'sticky left-0 z-[5] border border-violet-500/10 px-3 py-2 text-xs font-medium whitespace-nowrap',
                      isRowHighlight
                        ? 'bg-emerald-950/40 text-emerald-200'
                        : 'bg-slate-950/80 text-slate-300',
                    )}
                  >
                    <span className="font-mono text-[10px] text-slate-500 block">{artifactType.prefix}</span>
                    {artifactType.label}
                  </th>
                  {FLOW3_STEPS.map((step) => {
                    const cell = rowCells[step.id];
                    const cellKey = `${artifactType.id}:${step.id}`;
                    return (
                      <FlowMapCell
                        key={step.id}
                        cell={cell}
                        isHighlighted={step.id === highlightedStepId || isRowHighlight}
                        isExpanded={expandedCell === cellKey}
                        onToggle={() => toggleCell(artifactType.id, step.id)}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
